<script setup>
import { computed, ref } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import { Search, ChevronRight, Users } from 'lucide-vue-next'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageHero from '@/Components/Marketing/PageHero.vue'
import { Button } from '@/Components/ui/button'
import { tutors, becomeATutor } from '@/routes/index'
import { subjectIcon } from '@/lib/subjectIcon'

/**
 * Every subject Bindle teaches, with the number of tutors actually teaching it.
 *
 * A subject nobody teaches yet is still listed — it is part of the catalogue and
 * hiding it would misrepresent what we cover — but it is not a link, because the
 * listing behind it would be empty. It carries an invitation to apply instead,
 * which is the only useful thing that page can do for a visitor.
 */
const props = defineProps({
  subjects: { type: Array, default: () => [] },
  levels: { type: Array, default: () => [] },
  figures: { type: Object, default: () => ({}) },
})

const query = ref('')
const resultsRef = ref(null)

const filtered = computed(() => {
  const words = query.value.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return props.subjects

  return props.subjects.filter((subject) => {
    const haystack = `${subject.name} ${subject.description ?? ''}`.toLowerCase()

    return words.every((word) => haystack.includes(word))
  })
})

/**
 * Searching moved nothing on screen.
 *
 * The hero is tall enough that the first result sits below the fold — measured
 * at 770px on a 757px viewport — so typing a subject and pressing Enter left the
 * page looking untouched, and the search read as broken when it had in fact
 * matched. The count answers immediately, and Enter jumps to the results.
 */
const showResults = () => {
  resultsRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const taught = computed(() => filtered.value.filter((subject) => subject.tutor_count > 0))
const notYet = computed(() => filtered.value.filter((subject) => subject.tutor_count === 0))

const subjectUrl = (subject) => tutors.url({ query: { subject: subject.slug } })

const tutorLabel = (count) => `${count} ${count === 1 ? 'tutor' : 'tutors'}`
</script>

<template>
  <Head>
    <title>Subjects — Bindle</title>
    <meta name="description" content="Every subject Bindle tutors teach, from GCSE to A-Level, with the tutors who teach them." />
  </Head>

  <AppLayout>
    <PageHero
      eyebrow="Subjects"
      title="Every subject we teach, and the tutors who teach it."
      lead="Pick a subject to see the tutors who cover it, the levels they teach and what they charge. Nothing here is a placeholder — each count is the number of tutors currently taking students."
    >
      <!--
        The search box and the level pills are one row; the "what matched"
        message is a line under the whole row, not inside the search box.
        Inside it, the message made that one flex item two lines tall, and since
        the row centres its items the pills drifted below the middle of the
        input the moment anything was typed — the layout moved while being used.
      -->
      <div class="mt-8 space-y-2">
        <div class="flex flex-wrap items-center gap-3">
          <div class="relative w-full max-w-sm">
          <Search class="pointer-events-none absolute left-3 top-1/2 size-4.5 -translate-y-1/2 text-subtle" />
          <input
            v-model="query"
            type="search"
            placeholder="Search subjects"
            aria-label="Search subjects"
            class="h-11 w-full rounded-md border border-line-strong bg-surface pl-10 pr-3 text-[15px] text-ink placeholder:text-subtle focus:border-brand focus:outline-none focus:ring-3 focus:ring-brand/20"
              @keyup.enter="showResults"
            />
          </div>

          <!-- These were inert pills that looked exactly like buttons. They are
               now what they appeared to be: each filters the listing by level. -->
          <div class="flex flex-wrap gap-2">
            <Link
              v-for="level in levels"
              :key="level.id"
              :href="tutors.url({ query: { levels: [level.slug] } })"
              class="rounded-full border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-muted transition-colors hover:border-line-brand hover:bg-brand-soft hover:text-brand-soft-text"
            >{{ level.name }}</Link>
          </div>
        </div>

        <!-- Answers on the spot, so the search is visibly doing something
             even when every match is below the fold. -->
        <p v-if="query.trim()" class="text-sm text-muted" aria-live="polite">
          <template v-if="filtered.length">
            {{ filtered.length }} {{ filtered.length === 1 ? 'subject' : 'subjects' }} matching “{{ query }}” —
            <button type="button" class="font-semibold text-brand hover:underline" @click="showResults">
              jump to results
            </button>
          </template>
          <template v-else>No subject matches “{{ query }}”.</template>
        </p>
      </div>
    </PageHero>

    <!-- ==================== SUBJECTS WITH TUTORS ==================== -->
    <section ref="resultsRef" class="section scroll-mt-24">
      <div class="container-page">
        <div v-if="taught.length" class="mb-7 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 class="text-2xl md:text-3xl">Available now</h2>
            <p class="mt-2 text-[15px] text-muted">
              {{ taught.length }} {{ taught.length === 1 ? 'subject' : 'subjects' }} with tutors taking students today.
            </p>
          </div>
        </div>

        <div v-if="taught.length" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Link
            v-for="(subject, index) in taught"
            :key="subject.id"
            v-reveal="{ delay: Math.min(index, 8) * 40 }"
            :href="subjectUrl(subject)"
            class="group flex items-start gap-4 rounded-xl border border-line bg-surface p-5 transition duration-200 ease-brand hover:-translate-y-0.5 hover:border-line-brand hover:shadow-md"
          >
            <span class="grid size-12 shrink-0 place-items-center rounded-full bg-brand-soft text-brand transition-transform duration-200 ease-brand group-hover:scale-110">
              <span class="material-symbols-outlined text-2xl" aria-hidden="true">{{ subjectIcon(subject) }}</span>
            </span>
            <span class="min-w-0 flex-1">
              <span class="flex items-center justify-between gap-2">
                <span class="font-display text-lg font-bold text-ink">{{ subject.name }}</span>
                <ChevronRight class="size-4 shrink-0 text-subtle transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-brand" />
              </span>
              <span class="mt-1 flex items-center gap-1.5 text-sm text-muted">
                <Users class="size-3.5" />
                {{ tutorLabel(subject.tutor_count) }}
              </span>
              <span v-if="subject.description" class="mt-2 line-clamp-2 block text-sm leading-relaxed text-muted">
                {{ subject.description }}
              </span>
            </span>
          </Link>
        </div>

        <p v-else class="rounded-xl border border-dashed border-line-strong p-10 text-center text-muted">
          No subject matches “{{ query }}”.
        </p>
      </div>
    </section>

    <!-- ==================== NOT YET COVERED ==================== -->
    <section v-if="notYet.length" class="border-t border-line bg-surface-2 py-14 md:py-20">
      <div class="container-page">
        <h2 class="text-2xl md:text-3xl">Not covered yet</h2>
        <p class="mt-2 max-w-2xl text-[15px] text-muted">
          These are on the Bindle curriculum but no tutor has taken them on. They are not links because
          there is nothing behind them yet — if you teach one, we would like to hear from you.
        </p>

        <ul class="mt-7 flex flex-wrap gap-2.5">
          <li
            v-for="subject in notYet"
            :key="subject.id"
            class="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-2 text-sm font-semibold text-muted"
          >
            <span class="material-symbols-outlined text-base text-subtle" aria-hidden="true">{{ subjectIcon(subject) }}</span>
            {{ subject.name }}
          </li>
        </ul>

        <!-- Only for visitors. Someone already signed in is a student or a
             tutor; neither wants a recruitment button on a subject list. -->
        <Link v-if="!$page.props.auth?.user" :href="becomeATutor()" class="mt-8 inline-block">
          <Button variant="secondary">Teach one of these</Button>
        </Link>
      </div>
    </section>
  </AppLayout>
</template>
